// ==========================================================
// Logos de las entidades de «Trabaja para el Estado»
// ==========================================================
// El listado resuelve el logo de cada fila en tres capas, de más específica a
// más genérica:
//
//   1. Logo propio subido desde el panel (tabla `entidad_logos`).
//   2. Icono según el TIPO de entidad, deducido del nombre (acá abajo).
//   3. Avatar con las iniciales de la entidad.
//
// La capa 2 es la que hace esto viable: `trabajos_estado` trae 488 entidades
// distintas y 113 aparecen una sola vez, así que subir un logo por entidad no
// escala. Clasificando por tipo se cubre ~73 % de las filas sin subir nada.
//
// Ver supabase/migrations/202610310002_entidad_logos.sql.
import { supabase } from './supabaseClient';

const TABLA = 'entidad_logos';
const VISTA = 'entidades_convocatorias';
const BUCKET = 'logos-entidades';

// Lado del logo ya redimensionado. 128 px basta para un avatar de 40 px en
// pantallas 2x y deja los archivos en unos pocos KB.
const LADO = 128;

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutos

let cacheLogos = { mapa: null, timestamp: null, promesa: null };

export const vaciarCacheLogos = () => {
  cacheLogos = { mapa: null, timestamp: null, promesa: null };
};

// ==========================================================
// Clave estable
// ==========================================================
// Tiene que dar exactamente lo mismo que `normalizar_entidad()` en Supabase: el
// nombre llega como texto libre del scraper y cambia entre cargas (un punto, un
// acento, un espacio doble). Si la clave fuera el nombre literal, el logo se
// perdería en la siguiente carga.
export const normalizarClave = (nombre) =>
  String(nombre || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();

// ==========================================================
// Tipo de entidad
// ==========================================================
// El orden importa y no es alfabético: «MINISTERIO PUBLICO» es justicia, no un
// ministerio, así que justicia va antes. Igual «INSTITUTO NACIONAL DE SALUD»,
// que es salud y no educación.
const REGLAS_TIPO = [
  { tipo: 'justicia',     test: /PODER JUDICIAL|CORTE SUPERIOR|CORTE SUPREMA|MINISTERIO PUBLICO|FISCALIA|JUNTA NACIONAL DE JUSTICIA|TRIBUNAL|PROCURADURIA|INPE|INSTITUTO NACIONAL PENITENCIARIO/ },
  // UTES son las unidades territoriales de salud; vienen como «UTES N06 …».
  { tipo: 'salud',        test: /SALUD|HOSPITAL|ESSALUD|NEOPLASICAS|INSTITUTO NACIONAL DE REHABILITACION|SANIDAD|CLINICA|MATERNO|\bUTES\b/ },
  { tipo: 'defensa',      test: /EJERCITO|MARINA DE GUERRA|FUERZA AEREA|POLICIA|\bPNP\b|MINISTERIO DE DEFENSA|COMANDO CONJUNTO/ },
  { tipo: 'ugel',         test: /\bUGEL\b|GESTION EDUCATIVA LOCAL|UNIDAD DE GESTION EDUCATIVA/ },
  { tipo: 'universidad',  test: /UNIVERSIDAD|ASAMBLEA NACIONAL DE RECTORES|SUNEDU/ },
  { tipo: 'municipalidad', test: /MUNICIPALIDAD|MUNICIPAL/ },
  { tipo: 'regional',     test: /GOBIERNO REGIONAL|GOB REGIONAL|REGION \w+/ },
  // «MINIST. DE TRABAJO…» llega abreviado en varias filas.
  { tipo: 'ministerio',   test: /MINISTERIO|VICEMINISTERIO|\bMINIST\b/ },
  { tipo: 'programa',     test: /\bPROGRAMA\b|PROYECTO ESPECIAL|FONDO |PRONIED|QALI WARMA|CUNA MAS|PENSION 65|FONCODES|INABIF/ },
  { tipo: 'educacion',    test: /EDUCACION|COLEGIO|INSTITUTO DE EDUCACION|PEDAGOGIC|ESCUELA/ },
  // Organismos y entes adscritos: superintendencias, institutos nacionales,
  // consejos y registros. Va al final para que salud, justicia y defensa se
  // queden antes con los suyos («INSTITUTO NACIONAL PENITENCIARIO» es justicia).
  { tipo: 'organismo',    test: /SUPERINTENDENCIA|INSTITUTO NACIONAL|INSTITUTO GEOL|INDECOPI|INDECI|ORGANISMO|AUTORIDAD NACIONAL|CONSEJO NACIONAL|COMISION NACIONAL|SERVICIO NACIONAL|REGISTRO NACIONAL|AGENCIA |RENIEC|SUNAT|SUNARP|SUNEDU|SUNAFIL|OSCE|OEFA|SENASA|SENAMHI|\bINEI\b|CONCYTEC|CONADIS|MIGRACIONES|DEFENSA CIVIL/ }
];

export const TIPOS_ENTIDAD = {
  justicia:      { label: 'Justicia',          paleta: 2 },
  salud:         { label: 'Salud',             paleta: 5 },
  defensa:       { label: 'Defensa y orden',   paleta: 4 },
  ugel:          { label: 'UGEL',              paleta: 3 },
  universidad:   { label: 'Universidad',       paleta: 4 },
  municipalidad: { label: 'Municipalidad',     paleta: 1 },
  regional:      { label: 'Gobierno regional', paleta: 0 },
  ministerio:    { label: 'Ministerio',        paleta: 0 },
  programa:      { label: 'Programa social',   paleta: 1 },
  educacion:     { label: 'Educación',         paleta: 3 },
  organismo:     { label: 'Organismo público', paleta: 4 },
  otra:          { label: 'Otra entidad',      paleta: null }
};

// Devuelve el tipo de una entidad por su nombre. `otra` cuando ninguna regla
// encaja: esas filas se quedan con el avatar de iniciales.
export const tipoDeEntidad = (nombre) => {
  const clave = normalizarClave(nombre);
  if (!clave) return 'otra';
  const regla = REGLAS_TIPO.find(({ test }) => test.test(clave));
  return regla ? regla.tipo : 'otra';
};

// ==========================================================
// Lectura (portal)
// ==========================================================
// Un solo viaje para toda la tabla de logos: son cientos de filas de dos
// columnas, y el listado necesita resolver 20 logos por página.
const bajarLogos = async () => {
  const { data, error } = await supabase.from(TABLA).select('clave,url');
  if (error) throw error;

  const mapa = new Map();
  (data || []).forEach((fila) => {
    if (fila.clave && fila.url) mapa.set(fila.clave, fila.url);
  });
  return mapa;
};

export const obtenerLogos = (forzar = false) => {
  const vigente =
    cacheLogos.mapa && cacheLogos.timestamp && Date.now() - cacheLogos.timestamp < CACHE_DURATION;
  if (!forzar && vigente) return Promise.resolve(cacheLogos.mapa);
  if (!forzar && cacheLogos.promesa) return cacheLogos.promesa;

  const promesa = bajarLogos()
    .then((mapa) => {
      cacheLogos = { mapa, timestamp: Date.now(), promesa: null };
      return mapa;
    })
    .catch((e) => {
      cacheLogos.promesa = null;
      // Que falten los logos no puede tumbar el listado: se cae a los iconos
      // por tipo y a las iniciales.
      console.warn('No se pudieron leer los logos de las entidades:', e?.message || e);
      return new Map();
    });

  cacheLogos.promesa = promesa;
  return promesa;
};

// ==========================================================
// Panel de administración
// ==========================================================
export const listarEntidades = async () => {
  const { data, error } = await supabase
    .from(VISTA)
    .select('clave,nombre,convocatorias,abiertas,logo_url,logo_origen')
    .order('convocatorias', { ascending: false });
  if (error) throw error;
  return data || [];
};

// Redimensiona en el navegador antes de subir. Sin esto se acaba con PNG de
// 2 MB en una tabla que pinta 20 logos por página.
//
// Los SVG se suben tal cual: ya escalan y pesan poco, y pasarlos por canvas
// solo los rasterizaría.
export const prepararImagen = (archivo) =>
  new Promise((resolve, reject) => {
    if (!archivo) {
      reject(new Error('No hay archivo'));
      return;
    }
    if (archivo.type === 'image/svg+xml') {
      resolve({ blob: archivo, extension: 'svg', tipo: archivo.type });
      return;
    }

    const url = URL.createObjectURL(archivo);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        // Se encaja en un cuadrado de LADO sin deformar y sin recortar.
        const escala = Math.min(LADO / img.width, LADO / img.height, 1);
        const ancho = Math.max(1, Math.round(img.width * escala));
        const alto = Math.max(1, Math.round(img.height * escala));

        const lienzo = document.createElement('canvas');
        lienzo.width = ancho;
        lienzo.height = alto;
        const ctx = lienzo.getContext('2d');
        ctx.drawImage(img, 0, 0, ancho, alto);

        lienzo.toBlob(
          (blob) => {
            if (blob) resolve({ blob, extension: 'webp', tipo: 'image/webp' });
            else reject(new Error('No se pudo convertir la imagen'));
          },
          'image/webp',
          0.9
        );
      } catch (e) {
        reject(e);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('El archivo no es una imagen que el navegador pueda leer'));
    };

    img.src = url;
  });

// Guarda (o reemplaza) el logo de una entidad con un archivo.
export const subirLogo = async ({ clave, nombre, archivo }) => {
  const { blob, extension, tipo } = await prepararImagen(archivo);

  // El nombre del archivo lleva la marca de tiempo para que el navegador no
  // sirva el logo anterior desde su caché al reemplazarlo.
  const ruta = `${clave.toLowerCase().replace(/\s+/g, '-').slice(0, 80)}-${Date.now()}.${extension}`;

  const { error: errSubida } = await supabase.storage
    .from(BUCKET)
    .upload(ruta, blob, { contentType: tipo, upsert: true });
  if (errSubida) throw errSubida;

  const { data: publico } = supabase.storage.from(BUCKET).getPublicUrl(ruta);
  const url = publico?.publicUrl;
  if (!url) throw new Error('Supabase no devolvió la URL pública del logo');

  const anterior = await rutaAnterior(clave);
  await guardarFila({ clave, nombre, url, origen: 'archivo', ruta_archivo: ruta });
  // El archivo viejo se borra después de que la fila ya apunta al nuevo: si
  // falla el borrado queda basura en el bucket, no un logo roto en pantalla.
  if (anterior && anterior !== ruta) await borrarArchivo(anterior);

  vaciarCacheLogos();
  return url;
};

// Guarda el logo apuntando a una URL externa (la web de la entidad).
export const guardarLogoUrl = async ({ clave, nombre, url }) => {
  const limpia = String(url || '').trim();
  let parseada;
  try {
    parseada = new URL(limpia);
  } catch (e) {
    throw new Error('La dirección no es válida');
  }
  if (parseada.protocol !== 'https:') {
    throw new Error('La dirección tiene que empezar por https://');
  }

  const anterior = await rutaAnterior(clave);
  await guardarFila({ clave, nombre, url: parseada.href, origen: 'url', ruta_archivo: null });
  if (anterior) await borrarArchivo(anterior);

  vaciarCacheLogos();
  return parseada.href;
};

export const quitarLogo = async (clave) => {
  const anterior = await rutaAnterior(clave);
  const { error } = await supabase.from(TABLA).delete().eq('clave', clave);
  if (error) throw error;
  if (anterior) await borrarArchivo(anterior);
  vaciarCacheLogos();
};

// --- internos ---
const guardarFila = async (fila) => {
  const { error } = await supabase
    .from(TABLA)
    .upsert({ ...fila, actualizado_at: new Date().toISOString() }, { onConflict: 'clave' });
  if (error) throw error;
};

const rutaAnterior = async (clave) => {
  const { data } = await supabase.from(TABLA).select('ruta_archivo').eq('clave', clave).maybeSingle();
  return data?.ruta_archivo || null;
};

const borrarArchivo = async (ruta) => {
  const { error } = await supabase.storage.from(BUCKET).remove([ruta]);
  // No se propaga: un archivo huérfano en el bucket es inofensivo y no debe
  // hacer fracasar el guardado que ya se completó.
  if (error) console.warn('No se pudo borrar el logo anterior:', error.message);
};
